import React, { useState } from 'react';
import { ActivityIndicator } from 'react-native';
import styled from '@emotion/native';
import { useAuth } from '@/services/auth/AuthProvider';
import AuthService from '@/services/auth/AuthService';
import { green, lightGreen } from '@/shared/styles/colors';
import { BodyText } from '@/shared/components/StyledText';
import { Maybe, User } from '@/shared/types/generated';
import {
  SectionContainer,
  SectionHeader,
} from '@/shared/styles/styledComponents/settings';
import useStore from '@/stores/useStore';

interface Props {
  user: Maybe<User>;
}

const LoginDetailsSection = ({ user }: Props) => {
  const { getAccessToken } = useAuth();
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const { setSnackbarState } = useStore();

  const handlePasswordReset = async () => {
    if (!user?.email) {
      setSnackbarState({
        open: true,
        message: 'User email not found',
        severity: 'error',
      });
      return;
    }

    setIsResettingPassword(true);
    try {
      const accessToken = await getAccessToken();
      await AuthService.resetPassword(accessToken, user.email);

      setSnackbarState({
        open: true,
        message: 'Check your email for a link to reset your password.',
        severity: 'success',
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Network error. Please try again.';
      setSnackbarState({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <SectionContainer>
      <SectionHeader>Login Details:</SectionHeader>
      <LoginDetailsRow>
        <EmailText>{user?.email || 'No email found'}</EmailText>
        <ResetPasswordButton
          onPress={handlePasswordReset}
          disabled={isResettingPassword}
        >
          {isResettingPassword ? (
            <ActivityIndicator size="small" color={green} />
          ) : (
            <ResetPasswordText>Reset Password</ResetPasswordText>
          )}
        </ResetPasswordButton>
      </LoginDetailsRow>
    </SectionContainer>
  );
};

export default LoginDetailsSection;

const LoginDetailsRow = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 12,
});

const EmailText = styled(BodyText)({
  fontSize: 16,
  color: lightGreen,
  flex: 1,
});

const ResetPasswordButton = styled.TouchableOpacity({
  paddingHorizontal: 8,
  paddingVertical: 4,
  minWidth: 100,
  alignItems: 'center',
});

const ResetPasswordText = styled(BodyText)({
  fontSize: 16,
  color: green,
  textDecorationLine: 'underline',
  fontWeight: 'bold',
});
